import { z } from "zod";
import { PAGE_SIZE_MAX, paginationQuery, searchParam } from "../pagination";
import type { RoleCode } from "../auth";
import type { ShortlistRole } from "../shortlist";
import type { NocRef } from "./common";
import type { MeetEventSummary, MeetSportSummary } from "./meets";
import type { PersonGender } from "./participants";
import type { CompetitorStatus, MemberRole } from "./vocab";

/**
 * Athlete Manager — why an athlete is not on an OVR start list (the old
 * OVR's tool of the same name):
 *
 *   GET /athlete-manager?games_id=…   Paginated<AthleteManagerItem>
 *
 * The old tool repaired a registration table by hand. Here the chain is the
 * platform's: an athlete CARD (category `Aa`) is APPROVED in GMS → shortlisted
 * PRIMARY / RESERVE for an event (M7a) → entered in OVR (a `competitors` row
 * with the person on its roster). This read shows, per athlete card, where the
 * chain stops (`ATHLETE_PROBLEMS`). It writes nothing: the fixes are the
 * existing writes, each with its own rules — the card in GMS, the shortlist
 * (`PUT /shortlist/entries`), the entry (`POST /events/:id/competitors`).
 *
 * ADMIN / SUPER_ADMIN only (`ATHLETE_MANAGER_ROLES`) — a role, never a list
 * of named people (the old tool's six-name allowlist lived in code).
 * At least one of `search`, `responsible_organization_id`, `sport_id` is
 * required: the old tool did not search either until a filter was set.
 */

export const ATHLETE_MANAGER_ROLES = ["SUPER_ADMIN", "ADMIN"] as const satisfies readonly RoleCode[];
export const ATHLETE_MANAGER_PAGE_SIZE_DEFAULT = 25;
/** Events listed per athlete; `events_count` is always the true number. */
export const ATHLETE_MANAGER_EVENTS_MAX = 30;

/**
 * Where the chain stops, in chain order:
 *   NOT_APPROVED             the card is not APPROVED (GMS workflow)
 *   NO_SPORT                 the card names no sport (and not every sport)
 *   NOT_SHORTLISTED          APPROVED, but on no event's shortlist
 *   SHORTLISTED_NOT_ENTERED  shortlisted PRIMARY for a TEAM event with no OVR team holding them. In an
 *                            INDIVIDUAL event the shortlist row IS the entry (0073), so it cannot happen there
 *   ENTERED_NOT_SHORTLISTED  on an entry (as ATHLETE / RESERVE) of an event they are not shortlisted for,
 *                            not on a start list — older data, or a team
 *   START_LIST_NOT_SHORTLISTED  0073: on an INDIVIDUAL entry that is on a start list (or a phase draw, or
 *                            holds a medal) but no longer shortlisted, or the card left APPROVED — the
 *                            database kept the entry instead of removing it; an operator decides
 *   NO_SPORT_CLASS           para games only: no classification on the person
 */
export const ATHLETE_PROBLEMS = [
	"NOT_APPROVED",
	"NO_SPORT",
	"NOT_SHORTLISTED",
	"SHORTLISTED_NOT_ENTERED",
	"ENTERED_NOT_SHORTLISTED",
	"START_LIST_NOT_SHORTLISTED",
	"NO_SPORT_CLASS",
] as const;
export type AthleteProblem = (typeof ATHLETE_PROBLEMS)[number];

const idField = z.string().uuid().toLowerCase();

/** `problem`: one problem, `any` = at least one, omitted = every athlete card. By printed name. */
export const listAthleteManagerQuery = z
	.object({
		games_id: idField,
		search: searchParam.optional(),
		responsible_organization_id: idField.optional(),
		sport_id: idField.optional(),
		problem: z.enum([...ATHLETE_PROBLEMS, "any"]).optional(),
		page: paginationQuery.shape.page,
		page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(ATHLETE_MANAGER_PAGE_SIZE_DEFAULT),
	})
	.refine((q) => q.search !== undefined || q.responsible_organization_id !== undefined || q.sport_id !== undefined, {
		message: "Search for a name, or pick an NOC or a sport",
		path: ["search"],
	});
export type ListAthleteManagerQuery = z.input<typeof listAthleteManagerQuery>;

export interface AthleteManagerEvent {
	event: MeetEventSummary;
	sport: MeetSportSummary;
	/** The shortlist row for this event, or null. */
	shortlist_role: ShortlistRole | null;
	/** The OVR entry holding the person in this event, or null. */
	entry: { competitor_id: string; status: CompetitorStatus; member_role: MemberRole; responsible_organization_id: string } | null;
}

export interface AthleteManagerItem {
	ac_card_id: string;
	people_id: string;
	name: string;
	gender: PersonGender | null;
	registration_no: number;
	accreditation_no: number | null;
	/** The card's workflow status. */
	status: string;
	organization: NocRef | null;
	all_sports: boolean;
	sports: MeetSportSummary[];
	/** Every event the athlete is shortlisted for or entered in, by programme order. */
	events: AthleteManagerEvent[];
	events_count: number;
	/** Para games only; null elsewhere. */
	has_sport_class: boolean | null;
	problems: AthleteProblem[];
}
